import { NEUTRAL_INPUT, type VehicleInput } from './vehicleDriving';

/**
 * 게임패드 → 주행 입력 변환.
 *
 * 순수 함수만 둡니다. 브라우저 API(navigator.getGamepads, 진동 actuator)는 훅이
 * 담당하고, 여기서는 표준 매핑과 데드존·응답 곡선만 계산합니다. 그래야 축 값 처리
 * 규칙을 테스트할 수 있고, 프레임 루프에서 객체를 새로 만들지 않아도 됩니다.
 */

/** 스틱 중앙의 흔들림. 이 아래는 0으로 눌러 차가 저절로 꺾이지 않게 합니다. */
const STICK_DEADZONE = 0.12;
/** 트리거는 눌리지 않은 상태에서도 미세한 값을 보내는 경우가 있습니다. */
const TRIGGER_DEADZONE = 0.06;

/** 표준 레이아웃(Standard Gamepad) 기준 인덱스입니다. */
const AXIS_STEER = 0;
const BUTTON_RIGHT_TRIGGER = 7;
const BUTTON_LEFT_TRIGGER = 6;
/** A 버튼. 트리거가 없는 패드에서도 가속할 수 있게 둡니다. */
const BUTTON_THROTTLE_FALLBACK = 0;
/** B 버튼 = 후진, X 버튼 = 브레이크. 트리거 없는 패드의 대체 조작입니다. */
const BUTTON_REVERSE_FALLBACK = 1;
const BUTTON_BRAKE_FALLBACK = 2;

/** 데드존 밖 값을 0~1로 다시 펴서 임계점 근처의 급변을 없앱니다. */
export function applyDeadzone(value: number, deadzone: number): number {
  const magnitude = Math.abs(value);
  if (!Number.isFinite(value) || magnitude <= deadzone) {
    return 0;
  }
  const rescaled = (magnitude - deadzone) / (1 - deadzone);
  return Math.sign(value) * Math.min(1, rescaled);
}

/**
 * 조향 응답 곡선.
 *
 * 선형으로 두면 고속에서 스틱을 살짝만 움직여도 차가 크게 꺾입니다. 중앙 부근을
 * 완만하게 만들어 미세 조정이 가능하게 하고, 끝단에서는 최대 조향을 그대로 줍니다.
 */
export function applySteerCurve(value: number): number {
  return Math.sign(value) * Math.abs(value) ** 1.5;
}

interface GamepadLikeButton {
  pressed: boolean;
  value: number;
}

export interface GamepadLike {
  axes: readonly number[];
  buttons: readonly GamepadLikeButton[];
}

function buttonAmount(pad: GamepadLike, index: number): number {
  const button = pad.buttons[index];
  if (button === undefined) {
    return 0;
  }
  const value = Number.isFinite(button.value) ? button.value : 0;
  return Math.max(value, button.pressed ? 1 : 0);
}

/** 게임패드 하나를 아날로그 주행 입력으로 바꿉니다. */
export function readGamepadInput(pad: GamepadLike | null | undefined): VehicleInput | null {
  if (pad === null || pad === undefined) {
    return null;
  }

  const steerAxis = applyDeadzone(pad.axes[AXIS_STEER] ?? 0, STICK_DEADZONE);
  // 스틱 오른쪽(+)이 우회전입니다. heading은 좌회전이 양수라 부호를 뒤집습니다.
  const steer = -applySteerCurve(steerAxis);

  const rightTrigger = applyDeadzone(buttonAmount(pad, BUTTON_RIGHT_TRIGGER), TRIGGER_DEADZONE);
  const leftTrigger = applyDeadzone(buttonAmount(pad, BUTTON_LEFT_TRIGGER), TRIGGER_DEADZONE);
  const throttleFallback = buttonAmount(pad, BUTTON_THROTTLE_FALLBACK);
  const reverseFallback = buttonAmount(pad, BUTTON_REVERSE_FALLBACK);
  const brakeFallback = buttonAmount(pad, BUTTON_BRAKE_FALLBACK);

  const forward = Math.max(rightTrigger, throttleFallback);
  /*
   * 왼쪽 트리거는 정차 중에는 후진, 달리는 중에는 제동으로 쓰는 편이 흔하지만 그러면
   * 같은 입력의 의미가 속도에 따라 달라집니다. 여기서는 규약을 고정합니다.
   *   왼쪽 트리거 = 후진(아날로그), X 버튼 = 브레이크(아날로그)
   * 브레이크를 별도 버튼에 두면 "후진하려다 급제동"이 일어나지 않습니다.
   */
  const reverse = Math.max(leftTrigger, reverseFallback);
  const brake = Math.min(1, brakeFallback);

  return {
    throttle: Math.max(-1, Math.min(1, forward - reverse)),
    steer: Math.max(-1, Math.min(1, steer)),
    brake,
  };
}

/** 값이 실제로 들어온 쪽을 우선합니다. 키보드와 패드를 동시에 써도 서로를 지우지 않습니다. */
export function mergeDrivingInputs(
  digital: VehicleInput,
  analog: VehicleInput | null
): VehicleInput {
  if (analog === null) {
    return digital;
  }
  const pick = (digitalValue: number, analogValue: number) =>
    Math.abs(analogValue) >= Math.abs(digitalValue) ? analogValue : digitalValue;
  return {
    throttle: pick(digital.throttle, analog.throttle),
    steer: pick(digital.steer, analog.steer),
    brake: Math.max(digital.brake, analog.brake),
  };
}

export function isNeutralInput(input: VehicleInput): boolean {
  return (
    input.throttle === NEUTRAL_INPUT.throttle &&
    input.steer === NEUTRAL_INPUT.steer &&
    input.brake === NEUTRAL_INPUT.brake
  );
}
